import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Upload, Button, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { uploadImageToCloudinary } from "../../helpers/cloudinary"; // Import Cloudinary helper

interface AddEditSubcategoryModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (values: { name: string; image?: string }) => void; // Include image in onSubmit
  initialValues?: { name: string; image?: string }; // For editing
  isParent?: boolean; // Determine if it's a category (parent) or subcategory
}

const AddEditSubcategoryModal: React.FC<AddEditSubcategoryModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  initialValues,
  isParent = false, // Default to subcategory
}) => {
  const [form] = Form.useForm();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(
    initialValues?.image || null
  );

  // Reset form fields and image preview when modal is opened or initialValues change
  useEffect(() => {
    if (visible) {
      form.resetFields();
      if (initialValues) {
        form.setFieldsValue(initialValues);
        setImagePreview(initialValues.image || null);
      } else {
        setImagePreview(null);
      }
      setImageFile(null); // Reset image file when modal opens
    }
  }, [visible, initialValues, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      let imageUrl = initialValues?.image || undefined;

      // Upload image if a new file is provided
      if (imageFile) {
        const uploadedImageUrl = await uploadImageToCloudinary(imageFile);
        imageUrl = uploadedImageUrl || undefined;
      }

      onSubmit({ ...values, image: imageUrl }); // Pass both name and image to onSubmit
      form.resetFields();
      setImagePreview(null);
      setImageFile(null);
    } catch (error) {
      message.error("Please fill out the form correctly.");
    }
  };

  const handleImageUpload = (file: File) => {
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    return false; // Prevent default upload behavior
  };

  return (
    <Modal
      title={
        initialValues
          ? `Edit ${isParent ? "Category" : "Subcategory"}`
          : `Add ${isParent ? "Category" : "Subcategory"}`
      }
      visible={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label={`${isParent ? "Category" : "Subcategory"} Name`}
          rules={[{ required: true, message: "Please enter a name" }]}
        >
          <Input
            placeholder={`Enter ${isParent ? "category" : "subcategory"} name`}
          />
        </Form.Item>

        {isParent && ( // Show image upload only for categories
          <Form.Item label="Banner">
            <Upload
              beforeUpload={handleImageUpload}
              showUploadList={false}
              accept="image/*"
            >
              <Button icon={<UploadOutlined />}>Upload Image</Button>
            </Upload>
            {imagePreview && (
              <img
                src={imagePreview}
                alt="Preview"
                className="mt-2 h-20 w-20 object-cover rounded"
              />
            )}
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

export default AddEditSubcategoryModal;
